import { useState, useEffect, useCallback } from 'react'
import * as Tone from 'tone'
import { MoodCircle, type MoodSelection, type Mood, type NonBaseMood } from './MoodCircle'
import { stemsData } from '../data/stems'
import { config } from '../config'

/**
 * Map the numeric brand ID from the database to the brand folder name in stems.ts
 */
function mapBrandIdToFolder(brandId: string | number): string {
  // Convert brandId to string if it's a number
  const id = typeof brandId === 'number' ? brandId.toString() : brandId;
  
  // Map database IDs to folder names
  const brandMapping: Record<string, string> = {
    '1': 'brand_01',
    '2': 'brand_02',
    '3': 'brand_03'
  };
  
  return brandMapping[id] || id;
}

type StemType = 'main' | 'bass' | 'chord' | 'layer' | 'sfx'

interface Stem {
  path: string
  type: StemType
  variation: number
  severity: number
}

interface MixedStem {
  primary: Stem
  secondary?: Stem
  mixCoefficient: number
  variation: number
  severity: number
}

interface StemPlayer {
  primary: Tone.Player
  secondary?: Tone.Player
  primaryChannel: Tone.Channel
  secondaryChannel?: Tone.Channel
}

interface SelectedStems {
  [key: string]: Stem | MixedStem | null
}

interface Players {
  [key: string]: StemPlayer | null
}

interface UsedVariations {
  [key: string]: Record<StemType, number>
}

function constructStemUrl(brandId: string, mood: string, filename: string): string {
  return `${config.stemsBasePath}/${brandId}/${mood}/${encodeURIComponent(filename)}`;
}

export function SoundMixer({ brandId }: { brandId: string }) {
  // Convert brandId to the correct folder name
  const brandFolderId = mapBrandIdToFolder(brandId);
  
  const [availableStems, setAvailableStems] = useState<Record<string, Stem[]>>({})
  const [currentSelection, setCurrentSelection] = useState<MoodSelection | null>(null)
  const [selectedStems, setSelectedStems] = useState<Record<StemType, MixedStem | null>>({
    main: null,
    bass: null,
    chord: null,
    layer: null,
    sfx: null
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAudioContextStarted, setIsAudioContextStarted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [players, setPlayers] = useState<Players>({
    main: null,
    bass: null,
    chord: null,
    layer: null,
    sfx: null
  })
  const [lastPlayedSelection, setLastPlayedSelection] = useState<MoodSelection | null>(null)
  const [lastUsedVariations, setLastUsedVariations] = useState<UsedVariations>({})
  const [lastPlayedStems, setLastPlayedStems] = useState<Record<StemType, MixedStem | null> | null>(null)

  // Get the brand data
  const brand = stemsData.brands[brandFolderId as keyof typeof stemsData.brands];

  // Create a mixer for all stems
  const [mixer] = useState(() => {
    const mix = new Tone.Channel({
      volume: -6,
      mute: false
    })
    mix.toDestination()
    console.log('Mixer created and connected to destination')
    return mix
  })
  
  // Create a recorder for exporting
  const [recorder] = useState(() => {
    const dest = Tone.context.createMediaStreamDestination()
    const rec = new Tone.Recorder()
    return { recorder: rec, destination: dest }
  })

  // Load available stems for the current mood and power (skip for random mixes)
  useEffect(() => {
    if (!currentSelection || !brandFolderId) return

    // Skip loading availableStems for random mixes since they don't need them
    if (currentSelection.mood === 'random') {
      handleStop()
      return
    }

    const { mood, power } = currentSelection
    const stems: Record<string, Stem[]> = {}
    const stemTypes: StemType[] = ['main', 'bass', 'chord', 'layer', 'sfx']

    const brand = stemsData.brands[brandFolderId as keyof typeof stemsData.brands]
    if (!brand) return

    stemTypes.forEach(type => {
      stems[type] = []
      const moodStems = brand.stems[mood as keyof typeof brand.stems]
      if (!moodStems) return
      
      const typeStems = moodStems[type]
      if (!typeStems) return
      
      const powerData = typeStems.find((p: { power: number; variations: number[] }) => p.power === power)
      if (!powerData) return
      
      powerData.variations.forEach((variation: number) => {
        stems[type].push({
          path: `${mood}_${power}_${variation}_${type}`,
          type,
          variation,
          severity: 1
        })
      })
    })

    setAvailableStems(stems)
    handleStop()
  }, [currentSelection, brandFolderId])

  // Add a flag to prevent auto-generation during replay
  const [isReplaying, setIsReplaying] = useState(false)

  // Generate new mix when available stems change (but not for random mixes)
  useEffect(() => {
    if (Object.keys(availableStems).length > 0 && 
        currentSelection?.mood !== 'random') {
      handleGenerate()
    }
  }, [availableStems])

  const isStem = (stem: Stem | MixedStem | null): stem is Stem => {
    return stem !== null && 'variation' in stem && !('primary' in stem)
  }

  const isMixedStem = (stem: Stem | MixedStem | null): stem is MixedStem => {
    return stem !== null && 'primary' in stem
  }

  const isStemPlayer = (player: StemPlayer | null): player is StemPlayer => {
    return player !== null && 'primary' in player
  }

  const getStemVariation = (stem: Stem | MixedStem | null): number | null => {
    if (isStem(stem)) {
      return stem.variation
    }
    if (isMixedStem(stem)) {
      return stem.primary.variation
    }
    return null
  }

  function createMixedStem(primary: Stem, secondary: Stem | null = null, mixCoefficient = 0.5): MixedStem {
    return {
      primary,
      secondary: secondary || undefined,
      mixCoefficient,
      variation: primary.variation,
      severity: primary.severity
    }
  }

  function getStem(stems: Stem[], lastUsedVariation?: number): Stem | null {
    if (!stems || stems.length === 0) return null
    
    // Only use variations that actually exist in the stems array
    const availableVariations = stems.map(s => s.variation)
    
    // If last used variation exists in available variations, use it
    // Otherwise randomly select from available variations
    const selectedVariation = lastUsedVariation !== undefined && availableVariations.includes(lastUsedVariation)
      ? lastUsedVariation
      : availableVariations[Math.floor(Math.random() * availableVariations.length)]

    return stems.find(s => s.variation === selectedVariation) || null
  }

  function getSecondaryVariation(
    brandId: string, 
    mood: string, 
    type: StemType, 
    power: number, 
    desiredVariation?: number
  ): number | null {
    const brand = stemsData.brands[brandFolderId as keyof typeof stemsData.brands]
    if (!brand) return null

    const moodStems = brand.stems[mood as keyof typeof brand.stems]
    if (!moodStems) return null

    const typeStems = moodStems[type]
    if (!typeStems) return null

    const powerData = typeStems.find(p => p.power === power)
    if (!powerData) return null

    // If desired variation exists, use it
    if (desiredVariation !== undefined && powerData.variations.includes(desiredVariation)) {
      return desiredVariation
    }

    // Otherwise randomly select from available variations
    const availableVariations = powerData.variations
    return availableVariations[Math.floor(Math.random() * availableVariations.length)]
  }

  function shouldUsePrimary(mixCoefficient: number): boolean {
    return Math.random() > mixCoefficient
  }

  const handleGenerate = async (selectionOverride?: MoodSelection) => {
    const selection = selectionOverride || currentSelection;
    console.log('HANDLE_GENERATE: Called with selection =', selection);
    if (!selection) return

    try {
      setIsLoading(true)
      // Initialize audio context if needed
      if (!isAudioContextStarted) {
        const success = await initializeAudio()
        if (!success) return
      }

      // Stop current playback if any
      if (isPlaying) {
        handleStop()
      }

      const stemTypes: StemType[] = ['main', 'bass', 'chord', 'layer', 'sfx']
      let newSelectedStems: Record<StemType, MixedStem | null>
      
      console.log('GENERATE: Creating new stem combination')
      // Generate new stems
      const mixKey = `${selection.mood}_${selection.power}`
      newSelectedStems = {} as Record<StemType, MixedStem | null>
      const newUsedVariations: UsedVariations = {}

        // First, try to get stems for all types
        let allStemsSelected = true
        
        if (selection.mood === 'random') {
          // Random mix - pick random stems from all available moods and powers
          const brand = stemsData.brands[brandFolderId as keyof typeof stemsData.brands];
          if (!brand) {
            setError('Brand data not found')
            return
          }

          // Get all available moods (excluding base for more interesting mixes)
          const availableMoods = Object.keys(brand.stems).filter(mood => mood !== 'base');
          if (availableMoods.length === 0) {
            setError('No moods available for random mix')
            return
          }

          console.log('Generating random mix with stems from different moods and powers...')

          stemTypes.forEach(type => {
            // Find all available combinations for this stem type
            const availableCombinations: Array<{mood: string, power: number, variation: number}> = []
            
            for (const mood of availableMoods) {
              const moodStems = brand.stems[mood as keyof typeof brand.stems]
              if (!moodStems) continue
              
              const typeStems = moodStems[type]
              if (!typeStems || typeStems.length === 0) continue
              
              for (const powerData of typeStems) {
                for (const variation of powerData.variations) {
                  availableCombinations.push({
                    mood,
                    power: powerData.power,
                    variation
                  })
                }
              }
            }

            if (availableCombinations.length === 0) {
              console.warn(`No available combinations for stem type: ${type}`)
              allStemsSelected = false
              return
            }

            // Randomly select one combination
            const randomCombination = availableCombinations[Math.floor(Math.random() * availableCombinations.length)]

            // Create the stem object
            const stem: Stem = {
              path: `${randomCombination.mood}_${randomCombination.power}_${randomCombination.variation}_${type}`,
              type,
              variation: randomCombination.variation,
              severity: 1
            }

            newSelectedStems[type] = {
              primary: stem,
              secondary: undefined,
              mixCoefficient: 0,
              variation: stem.variation,
              severity: stem.severity
            }

            console.log(`${type}: ${randomCombination.mood} Power ${randomCombination.power} Variation ${randomCombination.variation}`)
          })
        } else if (selection.mood === 'base') {
          // Base mood - no mixing
        stemTypes.forEach(type => {
            const stem = getStem(availableStems[type], lastUsedVariations[mixKey]?.[type])
          if (!stem) {
            allStemsSelected = false
              return
            }
            newSelectedStems[type] = {
              primary: stem,
              secondary: undefined,
              mixCoefficient: 0,
              variation: stem.variation,
              severity: stem.severity
            }
          })
        } else {
          // Non-base mood - handle mixing
          stemTypes.forEach(type => {
            // Get primary stem from current power level
            const primaryMixKey = `${selection.mood}_${selection.power}`
            const primaryStem = getStem(availableStems[type], lastUsedVariations[primaryMixKey]?.[type])
            if (!primaryStem) {
              allStemsSelected = false
              console.error(`Failed to select primary stem for type: ${type}`)
              return
            }

            let secondaryStem: Stem | null = null
            if (selection.moodMixing) {
              // Get stem from secondary mood
              const secondaryMood = selection.moodMixing.secondaryMood
              const secondaryPower = selection.power
              
              // Get available variation from stems data
              const secondaryVariation = getSecondaryVariation(
                brandFolderId,
                secondaryMood,
                type,
                secondaryPower,
                undefined
              )

              if (secondaryVariation !== null && shouldUsePrimary(selection.moodMixing.mixCoefficient || 0.5)) {
                // Use primary stem (70% chance if mixCoefficient is 0.3)
                secondaryStem = {
                  path: `${secondaryMood}_${secondaryPower}_${secondaryVariation}_${type}`,
                  type,
                  variation: secondaryVariation,
                  severity: 1
                }
              }
            } else if (selection.powerMixing) {
              // Get stem from secondary power level
              const secondaryPower = selection.powerMixing.secondaryPower
              
              // Get available variation from stems data
              const secondaryVariation = getSecondaryVariation(
                brandFolderId,
                selection.mood,
                type,
                secondaryPower,
                undefined
              )

              if (secondaryVariation !== null && shouldUsePrimary(selection.powerMixing.mixCoefficient || 0.5)) {
                // Use primary stem
                secondaryStem = {
                  path: `${selection.mood}_${secondaryPower}_${secondaryVariation}_${type}`,
                  type,
                  variation: secondaryVariation,
                  severity: 1
                }
              }
            }

            const mixCoefficient = selection.powerMixing?.mixCoefficient || 
                                 selection.moodMixing?.mixCoefficient || 
                                 0.5
            newSelectedStems[type] = createMixedStem(primaryStem, secondaryStem, mixCoefficient)

            // Initialize variation tracking for each mix key if needed
            if (selection.powerMixing) {
              const primaryMixKey = `${selection.mood}_${selection.powerMixing.primaryPower}`
              const secondaryMixKey = `${selection.mood}_${selection.powerMixing.secondaryPower}`
              
              if (!newUsedVariations[primaryMixKey]) {
                newUsedVariations[primaryMixKey] = {
                  main: 0, bass: 0, chord: 0, layer: 0, sfx: 0
                }
              }
              if (!newUsedVariations[secondaryMixKey]) {
                newUsedVariations[secondaryMixKey] = {
                  main: 0, bass: 0, chord: 0, layer: 0, sfx: 0
                }
              }
              
              newUsedVariations[primaryMixKey][type] = primaryStem.variation
              if (secondaryStem) {
                newUsedVariations[secondaryMixKey][type] = secondaryStem.variation
              }
            } else {
              if (!newUsedVariations[primaryMixKey]) {
                newUsedVariations[primaryMixKey] = {
                  main: 0, bass: 0, chord: 0, layer: 0, sfx: 0
                }
              }
              newUsedVariations[primaryMixKey][type] = primaryStem.variation
            }
          })
        }

        // If any stem selection failed, stop here
        if (!allStemsSelected) {
          setError('Failed to select all required stems')
          return
        }

        // Update last used variations for this mix
        setLastUsedVariations(prev => ({
          ...prev,
          [mixKey]: newUsedVariations[mixKey]
        }))

      setSelectedStems(newSelectedStems)



      const newPlayers: Players = {
        main: null,
        bass: null,
        chord: null,
        layer: null,
        sfx: null
      }

      // Load and play selected stems
      let loadErrors = 0

      console.log('Loading stems...')
      const loadPromises = stemTypes.map(type => {
        return new Promise<void>((resolve, reject) => {
          const stemData = newSelectedStems[type]
          if (!stemData || !isMixedStem(stemData)) {
            console.log(`No stem selected for type: ${type}`)
            loadErrors++
            resolve()
            return
          }

          try {
            // Create channel for primary stem
            const primaryChannel = new Tone.Channel({
              volume: -6,
              mute: false
            })
            primaryChannel.connect(mixer)

            // Create URL for primary stem
            let primaryFilename: string
            let primaryStemUrl: string
            
            if (selection.mood === 'random') {
              // For random mixes, extract mood and power from the stem path
              const pathParts = stemData.primary.path.split('_')
              if (pathParts.length >= 4) {
                const stemMood = pathParts[0]
                const stemPower = pathParts[1]
                const stemVariation = pathParts[2]
                primaryFilename = `${stemMood}_${stemPower}_${stemVariation}_${type}.wav`
                primaryStemUrl = constructStemUrl(brandFolderId, stemMood, primaryFilename)
              } else {
                console.error(`Invalid stem path format: ${stemData.primary.path}`)
                loadErrors++
                resolve()
                return
              }
            } else {
              // Regular mood - all stems from same mood
              const primaryPower = selection.powerMixing?.primaryPower || selection.power
              primaryFilename = `${selection.mood}_${primaryPower}_${stemData.primary.variation}_${type}.wav`
              primaryStemUrl = constructStemUrl(brandFolderId, selection.mood, primaryFilename)
            }

            // Create player for primary stem
            const primaryPlayer = new Tone.Player({
              url: primaryStemUrl,
              autostart: false,
              loop: false,
              onload: () => {
                console.log(`Successfully loaded primary: ${primaryStemUrl}`)
                primaryPlayer.connect(primaryChannel)
                
                if (stemData.secondary) {
                  if (selection.moodMixing?.secondaryMood) {
                    // Get stem from secondary mood
                    const secondaryMood = selection.moodMixing.secondaryMood
                    const secondaryPower = selection.power
                    
                    // Get available variation from stems data
                    const secondaryVariation = getSecondaryVariation(
                      brandFolderId,
                      secondaryMood,
                      type,
                      secondaryPower,
                      undefined
                    )

                    if (secondaryVariation !== null && shouldUsePrimary(selection.moodMixing.mixCoefficient || 0.5)) {
                      // Use primary stem (70% chance if mixCoefficient is 0.3)
                      newPlayers[type] = {
                        primary: primaryPlayer,
                        primaryChannel
                      }
                      resolve()
                    } else if (secondaryVariation !== null) {
                      // Use secondary stem (30% chance if mixCoefficient is 0.3)
                      const secondaryFilename = `${secondaryMood}_${secondaryPower}_${secondaryVariation}_${type}.wav`
                      const secondaryStemUrl = constructStemUrl(brandFolderId, secondaryMood, secondaryFilename)

                      // Create channel for secondary stem (at full volume since it's the only one playing)
                      const secondaryChannel = new Tone.Channel({
                        volume: -6,
                        mute: false
                      })
                      secondaryChannel.connect(mixer)

                      // Create player for secondary stem
                      const secondaryPlayer = new Tone.Player({
                        url: secondaryStemUrl,
                        autostart: false,
                        loop: false,
                        onload: () => {
                          console.log(`Successfully loaded secondary: ${secondaryStemUrl}`)
                          secondaryPlayer.connect(secondaryChannel)
                          
                          newPlayers[type] = {
                            primary: secondaryPlayer,  // Use as primary since it's the only one
                            primaryChannel: secondaryChannel
                          }
                resolve()
              },
              onerror: (error) => {
                          console.error(`Error loading secondary stem: ${secondaryStemUrl}`, error)
                          loadErrors++
                          resolve()
                        }
                      })
                    } else {
                      // No valid secondary variation found, use primary
                      newPlayers[type] = {
                        primary: primaryPlayer,
                        primaryChannel
                      }
                      resolve()
                    }
                  } else if (selection.powerMixing) {
                    // Similar logic for power mixing
                    const secondaryPower = selection.powerMixing.secondaryPower
                    const mixCoefficient = selection.powerMixing.mixCoefficient || 0.5
                    
                    const secondaryVariation = getSecondaryVariation(
                      brandFolderId,
                      selection.mood,
                      type,
                      secondaryPower,
                      undefined
                    )

                    if (secondaryVariation !== null && shouldUsePrimary(mixCoefficient)) {
                      // Use primary stem
                      newPlayers[type] = {
                        primary: primaryPlayer,
                        primaryChannel
                      }
                      resolve()
                    } else if (secondaryVariation !== null) {
                      // Use secondary stem
                      const secondaryFilename = `${selection.mood}_${secondaryPower}_${secondaryVariation}_${type}.wav`
                      const secondaryStemUrl = constructStemUrl(brandFolderId, selection.mood, secondaryFilename)

                      // Create channel for secondary stem
                      const secondaryChannel = new Tone.Channel({
                        volume: -6,
                        mute: false
                      })
                      secondaryChannel.connect(mixer)

                      // Create player for secondary stem
                      const secondaryPlayer = new Tone.Player({
                        url: secondaryStemUrl,
                        autostart: false,
                        loop: false,
                        onload: () => {
                          console.log(`Successfully loaded secondary: ${secondaryStemUrl}`)
                          secondaryPlayer.connect(secondaryChannel)
                          
                          newPlayers[type] = {
                            primary: secondaryPlayer,  // Use as primary since it's the only one
                            primaryChannel: secondaryChannel
                          }
                          resolve()
                        },
                        onerror: (error) => {
                          console.error(`Error loading secondary stem: ${secondaryStemUrl}`, error)
                          loadErrors++
                          resolve()
                        }
                      })
                    } else {
                      // No valid secondary variation found, use primary
                      newPlayers[type] = {
                        primary: primaryPlayer,
                        primaryChannel
                      }
                      resolve()
                    }
                  }
                } else {
                  newPlayers[type] = {
                    primary: primaryPlayer,
                    primaryChannel
                  }
                  resolve()
                }
              },
              onerror: (error) => {
                console.error(`Error loading primary stem: ${primaryStemUrl}`, error)
                loadErrors++
                resolve()
              }
            })
          } catch (err) {
            console.error(`Error creating player for stem type ${type}:`, err)
            loadErrors++
            resolve()
          }
        })
      })

      // Wait for all players to load
      await Promise.all(loadPromises)
      console.log('All stems loaded')

      if (loadErrors > 0) {
        setError(`Failed to load ${loadErrors} sound${loadErrors > 1 ? 's' : ''}`)
        return
      }

      // Set players before starting them
      setPlayers(newPlayers)

      // Make sure context is running
      if (Tone.context.state !== 'running') {
        await Tone.context.resume()
      }

      // Start all players
      Object.values(newPlayers).forEach(player => {
        if (player) {
          if ('primary' in player) {
            player.primary.start()
            if (player.secondary) {
              player.secondary.start()
            }
          }
        }
      })

      setIsPlaying(true)
      setError(null)
      
      // Store this combination for future replay
      console.log('STORING: Saving stems for future replay:', newSelectedStems);
      console.log('STORING: Saving selection for future replay:', selection);
      setLastPlayedStems(newSelectedStems)
      setLastPlayedSelection(selection)
    } catch (err) {
      console.error('Error in handleGenerate:', err)
      setError('Failed to generate mix')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStop = useCallback(() => {
    try {
      Object.values(players).forEach(player => {
        if (isStemPlayer(player)) {
          try {
            player.primary.stop()
            player.primary.disconnect()
            player.primaryChannel.disconnect()
            if (player.secondary && player.secondaryChannel) {
              player.secondary.stop()
              player.secondary.disconnect()
              player.secondaryChannel.disconnect()
            }
          } catch (err) {
            console.error('Error stopping player:', err)
          }
        }
      })
      
      setPlayers({
        main: null,
        bass: null,
        chord: null,
        layer: null,
        sfx: null
      })
      setIsPlaying(false)
      setError(null)
    } catch (err) {
      console.error('Error in handleStop:', err)
    }
  }, [players])

  const initializeAudio = async () => {
    await Tone.start();
    console.log('Audio context started');
      setIsAudioContextStarted(true)
      setError(null)
      return true
  }

  useEffect(() => {
    // Add click listener to the document
    const handleClick = async () => {
      if (Tone.context.state !== 'running') {
        await initializeAudio();
      }
    };
    
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const [exportProgress, setExportProgress] = useState<string>('')
  const [isCancelled, setIsCancelled] = useState(false)

  const cleanupExport = () => {
    setIsExporting(false)
    setExportProgress('')
    setIsCancelled(false)
  }

  const handleExport = async () => {
    if (!currentSelection || !brandFolderId) return

    try {
      setIsExporting(true)
      setError(null)
      setExportProgress('Starting export...')

      // Initialize audio context if needed
      if (!isAudioContextStarted) {
        setExportProgress('Initializing audio...')
        const success = await initializeAudio()
        if (!success) {
          throw new Error('Please click again to enable audio')
        }
      }

      const stemTypes: StemType[] = ['main', 'bass', 'chord', 'layer', 'sfx']
      setExportProgress('Loading audio files...')
      
      // Create an offline context for rendering
      const duration = 4.7 // slightly longer than our stems
      const sampleRate = 44100
      const offlineCtx = new OfflineAudioContext({
        numberOfChannels: 2,
        length: Math.ceil(duration * sampleRate),
        sampleRate: sampleRate
      })

      // Load and decode all stems
      const bufferPromises = stemTypes.map(async (type, index) => {
        const stem = selectedStems[type]
        if (!stem) return null

        try {
          setExportProgress(`Loading ${type} stem (${index + 1}/${stemTypes.length})...`)
          console.log(`Fetching ${type} stem...`)
          
          // For random mix, extract mood and power from the stem path
          let filename: string
          let stemUrl: string
          
                     if (currentSelection.mood === 'random') {
            // Extract mood, power, and variation from the stem path
            // Format: "mood_power_variation_type"
            const pathParts = stem.primary.path.split('_')
            if (pathParts.length >= 4) {
              const stemMood = pathParts[0]
              const stemPower = pathParts[1]
              const stemVariation = pathParts[2]
              filename = `${stemMood}_${stemPower}_${stemVariation}_${type}.wav`
              stemUrl = constructStemUrl(brandFolderId, stemMood, filename)
            } else {
              throw new Error(`Invalid stem path format: ${stem.primary.path}`)
            }
          } else {
            // Regular mix - all stems from same mood
            filename = `${currentSelection.mood}_${currentSelection.power}_${stem.variation}_${type}.wav`
            stemUrl = constructStemUrl(brandFolderId, currentSelection.mood, filename)
          }
          
          const response = await fetch(stemUrl)
          if (!response.ok) {
            throw new Error(`HTTP error loading ${type} stem: ${response.status}`)
          }

          const arrayBuffer = await response.arrayBuffer()
          if (arrayBuffer.byteLength === 0) {
            throw new Error(`Empty response for ${type} stem`)
          }

          console.log(`Decoding ${type} stem (${arrayBuffer.byteLength} bytes)...`)
          const audioBuffer = await offlineCtx.decodeAudioData(arrayBuffer)
          console.log(`Successfully decoded ${type} stem: ${audioBuffer.duration}s, ${audioBuffer.numberOfChannels} channels`)
          
          return audioBuffer
        } catch (err) {
          console.error(`Error loading stem ${type}:`, err)
          return null
        }
      })

      const audioBuffers = (await Promise.all(bufferPromises)).filter(Boolean)
      
      if (audioBuffers.length === 0) {
        throw new Error('No audio files could be loaded')
      }

      setExportProgress('Mixing audio...')
      console.log('Starting audio mixing process...')

      // Create sources and connect them
      const sources = audioBuffers.map(buffer => {
        const source = offlineCtx.createBufferSource()
        const gain = offlineCtx.createGain()
        
        source.buffer = buffer
        source.connect(gain)
        gain.connect(offlineCtx.destination)
        
        // Set gain to prevent clipping
        gain.gain.value = 0.8 / stemTypes.length
        
        return source
      })

      // Start all sources
      sources.forEach(source => source.start(0))

      // Render audio
      console.log('Rendering audio...')
      const renderedBuffer = await offlineCtx.startRendering()
      console.log('Audio rendering complete')

      setExportProgress('Creating WAV file...')
      console.log('Creating WAV file...')

      // Convert to WAV
      const wavData = new Float32Array(renderedBuffer.length * 2)
      const left = renderedBuffer.getChannelData(0)
      const right = renderedBuffer.getChannelData(1)

      // Interleave channels
      for (let i = 0; i < renderedBuffer.length; i++) {
        wavData[i * 2] = left[i]
        wavData[i * 2 + 1] = right[i]
      }

      // Convert to 16-bit PCM
      const pcmData = new Int16Array(wavData.length)
      for (let i = 0; i < wavData.length; i++) {
        const s = Math.max(-1, Math.min(1, wavData[i]))
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }

      // Create WAV header
      const wavHeader = new ArrayBuffer(44)
      const view = new DataView(wavHeader)

      // "RIFF" chunk descriptor
      view.setUint8(0, 0x52) // R
      view.setUint8(1, 0x49) // I
      view.setUint8(2, 0x46) // F
      view.setUint8(3, 0x46) // F
      view.setUint32(4, 36 + pcmData.byteLength, true)
      view.setUint8(8, 0x57)  // W
      view.setUint8(9, 0x41)  // A
      view.setUint8(10, 0x56) // V
      view.setUint8(11, 0x45) // E

      // "fmt " sub-chunk
      view.setUint8(12, 0x66)  // f
      view.setUint8(13, 0x6D)  // m
      view.setUint8(14, 0x74)  // t
      view.setUint8(15, 0x20)  // " "
      view.setUint32(16, 16, true)  // fmt chunk size
      view.setUint16(20, 1, true)   // Audio format (1 for PCM)
      view.setUint16(22, 2, true)   // Number of channels
      view.setUint32(24, sampleRate, true)  // Sample rate
      view.setUint32(28, sampleRate * 4, true)  // Byte rate
      view.setUint16(32, 4, true)   // Block align
      view.setUint16(34, 16, true)  // Bits per sample

      // "data" sub-chunk
      view.setUint8(36, 0x64)  // d
      view.setUint8(37, 0x61)  // a
      view.setUint8(38, 0x74)  // t
      view.setUint8(39, 0x61)  // a
      view.setUint32(40, pcmData.byteLength, true)  // data chunk size

      console.log('Creating final blob...')
      const blob = new Blob([wavHeader, pcmData.buffer], { type: 'audio/wav' })
      
      const fileSize = blob.size
      console.log(`WAV file size: ${fileSize} bytes`)
      
      if (fileSize === 0) {
        throw new Error('Generated WAV file is empty')
      }

      setExportProgress(`Creating ${Math.round(fileSize / 1024)} KB WAV file...`)

      // Create object URL
      console.log('Creating object URL...')
      const url = URL.createObjectURL(blob)

      // Trigger download using a more compatible method
      console.log('Initiating download...')
      setExportProgress('Starting download...')
      
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = url
      link.download = `tududum_${currentSelection.mood === 'random' ? 'random_mix' : `${currentSelection.mood}_${currentSelection.power}`}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up
      console.log('Cleaning up...')
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 1000)
      
      setExportProgress('Export complete!')
      console.log('Export process completed')
      
    } catch (err) {
      console.error('Error in handleExport:', err)
      setError(`Error exporting audio: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setTimeout(() => {
        setIsExporting(false)
        setExportProgress('')
      }, 2000)
    }
  }

  const handleReplay = async () => {
    if (!lastPlayedStems || !players || Object.values(players).every(p => p === null)) {
      console.log('REPLAY: No previous mix or players to replay');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Initialize audio context if needed
      if (!isAudioContextStarted) {
        const success = await initializeAudio()
        if (!success) return
      }

      // Stop current playback if any, but don't clear players
      if (isPlaying) {
        Object.values(players).forEach(player => {
          if (player && player.primary) {
            try {
              player.primary.stop()
              if (player.secondary) {
                player.secondary.stop()
              }
            } catch (err) {
              console.error('Error stopping player for replay:', err)
            }
          }
        })
        setIsPlaying(false)
      }

      console.log('REPLAY: Restarting existing players');
      
      // Make sure context is running
      if (Tone.context.state !== 'running') {
        await Tone.context.resume()
      }

      // Start all existing players
      Object.values(players).forEach(player => {
        if (player && player.primary) {
          try {
            player.primary.start()
            if (player.secondary) {
              player.secondary.start()
            }
          } catch (err) {
            console.error(`Error starting replay player:`, err)
          }
        }
      })

      setIsPlaying(true)
      setError(null)
      
      // Set the UI to show the last played selection (but not for random mixes)
      if (lastPlayedSelection && lastPlayedSelection.mood !== 'random') {
        setCurrentSelection(lastPlayedSelection)
      }
      setSelectedStems(lastPlayedStems)

    } catch (err) {
      console.error('Error in handleReplay:', err)
      setError('Failed to replay mix')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStemSelect = async (type: StemType, power: number, variation: number) => {
    if (!currentSelection) return

    try {
      // Stop current playback if any
      if (isPlaying) {
        handleStop()
      }

      // Update selection
      const newSelection = {
        ...currentSelection,
        [type]: { power, variation }
      }
      setCurrentSelection(newSelection)

      // Initialize audio context if needed
      await initializeAudio()

      // Load and start the new stem
      const filename = `${currentSelection.mood}_${power}_${variation}_${type}.wav`
      const stemUrl = constructStemUrl(brandFolderId, currentSelection.mood, filename)

      const channel = new Tone.Channel({
        volume: -6,
        mute: false
      })
      channel.connect(mixer)

      const player = new Tone.Player({
        url: stemUrl,
        onload: () => {
          const stemPlayer: StemPlayer = {
            primary: player,
            primaryChannel: channel
          }
          setPlayers(prev => ({
            ...prev,
            [type]: stemPlayer
          }))
          player.start()
          setIsPlaying(true)
        },
        onerror: (error) => {
          console.error(`Error loading stem ${stemUrl}:`, error)
          setError(`Failed to load ${type} stem`)
        }
      })
    } catch (err) {
      console.error('Error in handleStemSelect:', err)
      setError('Failed to play stem')
    }
  }





  // Check if brand has any stems by looking at the stems data structure
  const hasSounds = brand && Object.values(brand.stems).some(mood => 
    Object.values(mood).some(stemType => 
      (stemType as { power: number; variations: number[] }[]).length > 0
    )
  );

  if (!hasSounds) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="text-4xl mb-4">🎵</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Sounds Available</h2>
          <p className="text-gray-600">
            This brand doesn't have any sounds yet.
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Please contact your manager to get started.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      {/* Main UI Section */}
      <div className="w-[500px] flex flex-col items-center gap-8">
        <div className="aspect-square relative">
          <MoodCircle 
            onMoodSelect={(selection) => {
              if (selection) {
                setCurrentSelection(selection);
                // Clear last played stems when mood/power changes
                if (!lastPlayedSelection || 
                    selection.mood !== lastPlayedSelection.mood || 
                    selection.power !== lastPlayedSelection.power) {
                  setLastPlayedStems(null);
                }
              }
            }}
            currentSelection={currentSelection ?? undefined}
            availableMoods={Object.keys(brand?.stems || {})}
          />
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-full">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 items-center">
          {lastPlayedStems && (
            <button
              onClick={async () => {
                console.log('REPLAY BUTTON: Replaying last mix');
                await handleReplay();
              }}
              disabled={isLoading || isExporting}
              className="px-5 py-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 font-medium text-sm flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0"/>
                <path d="m17 12-6-4v8z"/>
              </svg>
              Replay Last Mix
            </button>
          )}
          
          <button
            onClick={handleExport}
            disabled={!currentSelection || isExporting}
            className="px-5 py-1.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors disabled:opacity-50 font-medium text-sm"
          >
            {isExporting ? `Exporting... ${exportProgress}` : 'Export Mix'}
          </button>
        </div>

        {/* Random Mix button */}
        <button
          onClick={async () => {
            // Set a special "random" mood selection and call handleGenerate directly
            const randomSelection = {
              mood: 'random' as const,
              power: 1 as const // Power doesn't matter for random mixes
            }
            setCurrentSelection(randomSelection)
            await handleGenerate(randomSelection)
          }}
          disabled={isLoading}
          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Generating...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 18h1.4c1.3 0 2.5-.6 3.3-1.7l6.1-8.6c.7-1.1 2-1.7 3.3-1.7H22"/>
                <path d="m18 2 4 4-4 4"/>
                <path d="M2 6h1.9c1.5 0 2.9.9 3.6 2.2"/>
                <path d="M22 18h-5.9c-1.3 0-2.6-.7-3.3-1.8l-.5-.8"/>
                <path d="m18 14 4 4-4 4"/>
              </svg>
              Random Mix
            </>
          )}
        </button>

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}
      </div>
    </div>
  )
} 